import React, { useState } from 'react';
import reportService from '../../services/reportService';
import { useVehicles } from '../../hooks/useVehicles';
import { downloadBlob } from '../../utils/helpers';
import toast from 'react-hot-toast';

const parseFilenameFromDisposition = (disposition) => {
  if (!disposition) return '';
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1] || '';
};

const fallbackFilename = (filters, vehicleLabel, extension) => {
  const reportType = (filters.report_type || 'finance').replace(/\s+/g, '_').toLowerCase();
  const vehicle = (vehicleLabel || 'all_vehicles').replace(/\s+/g, '_').toLowerCase();
  const start = filters.start_date || 'all_time_start';
  const end = filters.end_date || 'all_time_end';
  const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
  return `mololine_${reportType}_${vehicle}_${start}_to_${end}_${stamp}.${extension}`;
};

export default function ReportGenerator() {
  const { vehicles } = useVehicles();
  const [filters, setFilters] = useState({
    vehicle: '', start_date: '', end_date: '', report_type: 'finance',
  });
  const [loading, setLoading] = useState({ pdf: false, excel: false });

  const handle = (e) => setFilters(p => ({ ...p, [e.target.name]: e.target.value }));

  const download = async (type) => {
    setLoading(p => ({ ...p, [type]: true }));
    try {
      let response;
      if (type === 'pdf') response = await reportService.downloadPDF(filters);
      else response = await reportService.downloadExcel(filters);

      const extension = type === 'pdf' ? 'pdf' : 'xlsx';
      const selectedVehicle = vehicles.find(v => String(v.id) === String(filters.vehicle));
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const filename = parseFilenameFromDisposition(contentDisposition)
        || fallbackFilename(filters, selectedVehicle?.registration_number, extension);
      const blob = response.data;
      downloadBlob(blob, filename);
      toast.success(`${type.toUpperCase()} downloaded`);
    } catch {
      toast.error('Failed to generate report');
    } finally { setLoading(p => ({ ...p, [type]: false })); }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Generate Report</h3>
      </div>
      <div className="card-body">
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Report Type</label>
            <select className="form-control" name="report_type" value={filters.report_type} onChange={handle}>
              <option value="finance">Financial Summary</option>
              <option value="trips">Trips Report</option>
              <option value="drivers">Driver Performance</option>
              <option value="expenses">Expense Breakdown</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Vehicle</label>
            <select className="form-control" name="vehicle" value={filters.vehicle} onChange={handle}>
              <option value="">All Vehicles</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number}</option>)}
            </select>
          </div>
        </div>
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input className="form-control" type="date" name="start_date" value={filters.start_date} onChange={handle} />
          </div>
          <div className="form-group">
            <label className="form-label">End Date</label>
            <input className="form-control" type="date" name="end_date" value={filters.end_date} onChange={handle} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => download('pdf')} disabled={loading.pdf}>
            {loading.pdf ? 'Generating…' : '⬇ Download PDF'}
          </button>
          <button className="btn btn-secondary" onClick={() => download('excel')} disabled={loading.excel}>
            {loading.excel ? 'Generating…' : '⬇ Download Excel'}
          </button>
        </div>
      </div>
    </div>
  );
}